import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  Line,
} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import sdate from "@timclark/sdate";
import {
  Phone,
  MessageCircle,
  Coffee,
  Video,
  Users as UsersIcon,
  FileText,
  CalendarDays,
  MapPin,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Target,
  Clock,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { net } from "../../../../database";
import Loading from "../../Loading";
import Lang from "../../components/Lang";
import { CenterContext, LanguageContext } from "../../../../Context";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const STAGE_ORDER = ["lead", "add-zalo", "book-test", "test", "enrol"];
const STAGE_LANG_KEYS = {
  lead: "pipeline.lead",
  "add-zalo": "pipeline.addZalo",
  "book-test": "pipeline.bookTest",
  test: "pipeline.test",
  enrol: "pipeline.enrol",
};
const STAGE_COLORS = {
  lead: "#64748b",
  "add-zalo": "#3b82f6",
  "book-test": "#f59e0b",
  test: "#f97316",
  enrol: "#10b981",
};

const METHOD_ICONS = {
  call: Phone,
  zalo: MessageCircle,
  visit: Coffee,
  video: Video,
  meeting: UsersIcon,
  other: FileText,
};
const METHOD_LANG_KEYS = {
  call: "pipeline.phoneCall",
  zalo: "pipeline.zalo",
  visit: "pipeline.visit",
  video: "pipeline.videoCall",
  meeting: "pipeline.meeting",
  other: "pipeline.other",
};
const METHOD_COLORS = {
  call: "#3b82f6",
  zalo: "#06b6d4",
  visit: "#f59e0b",
  video: "#8b5cf6",
  meeting: "#10b981",
  other: "#64748b",
};

const OUTCOME_LANG_KEYS = {
  noAnswer: "pipeline.noAnswer",
  leftMessage: "pipeline.leftMessage",
  interested: "pipeline.interested",
  notInterested: "pipeline.notInterested",
  callBackLater: "pipeline.callBackLater",
  bookedTest: "pipeline.bookedTest",
  followUp: "pipeline.followUp",
  completed: "pipeline.completed",
};

const OUTCOME_COLORS = {
  noAnswer: "#94a3b8",
  leftMessage: "#60a5fa",
  interested: "#10b981",
  notInterested: "#ef4444",
  callBackLater: "#f59e0b",
  bookedTest: "#8b5cf6",
  followUp: "#06b6d4",
  completed: "#22c55e",
};

/**
 * PipelineReport — Sales staff activity report for the pipeline.
 * Shows per-staff metrics: contacts made, stage advancements, conversion rates,
 * contact method/outcome breakdowns, daily trend, and monthly history.
 *
 * Uses URL search params (?period=&from=&to=&location=) for shareable state.
 * Fetches data from /api/pipeline/report/activity and /api/pipeline/report/activity/history.
 * Bilingual: English and Vietnamese via Lang component and LanguageContext.
 *
 * @component
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
export default function PipelineReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { locations } = useContext(CenterContext);
  const { l, language } = useContext(LanguageContext);
  const t = useCallback((key) => {
    try { return l[key]?.[language] || key; } catch { return key; }
  }, [l, language]);

  const periodParam = searchParams.get("period") || "week";
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const locationParam = searchParams.get("location") || "";

  const [reportData, setReportData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStaff, setExpandedStaff] = useState(null);

  const periodDates = useMemo(() => {
    const today = new sdate();
    if (periodParam === "custom" && fromParam && toParam) {
      return { from: fromParam, to: toParam };
    }
    if (periodParam === "today") {
      return { from: today.toString(), to: today.toString() };
    }
    if (periodParam === "month") {
      const start = new sdate(today.year(), today.month(), 1);
      const end = start.addMonths(1).addDays(-1);
      return { from: start.toString(), to: end.toString() };
    }
    const start = new sdate(today.startOfWeek());
    const end = start.addDays(6);
    return { from: start.toString(), to: end.toString() };
  }, [periodParam, fromParam, toParam]);

  useEffect(() => {
    fetchReport();
    fetchHistory();
  }, [periodDates.from, periodDates.to, locationParam]);

  async function fetchReport() {
    setLoading(true);
    setError(null);
    try {
      let url = `/pipeline/report/activity?from=${periodDates.from}&to=${periodDates.to}`;
      if (locationParam) url += `&location=${locationParam}`;
      const data = await net.get(url);
      if (data) {
        setReportData(data);
      } else {
        setReportData(null);
        setError(t("pipelineReport.noData"));
      }
    } catch (err) {
      setError(err.message || t("pipelineReport.failed"));
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }

  async function fetchHistory() {
    try {
      let url = `/pipeline/report/activity/history?months=6`;
      if (locationParam) url += `&location=${locationParam}`;
      const data = await net.get(url);
      if (data) setHistoryData(data.history || data);
    } catch {
      setHistoryData(null);
    }
  }

  const setPeriod = useCallback(
    (p) => {
      const params = new URLSearchParams(searchParams);
      params.set("period", p);
      if (p !== "custom") {
        params.delete("from");
        params.delete("to");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setCustomDate = useCallback(
    (field, value) => {
      const params = new URLSearchParams(searchParams);
      params.set("period", "custom");
      params.set(field, value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setLocation = useCallback(
    (id) => {
      const params = new URLSearchParams(searchParams);
      if (id) params.set("location", id);
      else params.delete("location");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const toggleExpand = useCallback(
    (staffId) => {
      setExpandedStaff((prev) => (prev === staffId ? null : staffId));
    },
    []
  );

  const staff = reportData?.staff || [];
  const dailyTrend = reportData?.dailyTrend || [];

  const summary = useMemo(() => {
    if (!staff.length) {
      return { contactsTotal: 0, advancementsTotal: 0, conversionRate: 0, avgDaysToEnrol: 0 };
    }
    const contactsTotal = staff.reduce((a, s) => a + (s.contactsTotal || 0), 0);
    const advancementsTotal = staff.reduce((a, s) => a + (s.stageAdvancements || 0), 0);
    const totalLeads = staff.reduce((a, s) => a + (s.leadsAdded || 0), 0);
    const enrolledLeads = staff.reduce(
      (a, s) => a + ((s.leadsByStage?.enrol || 0)), 0
    );
    const conversionRate = totalLeads > 0 ? enrolledLeads / totalLeads : 0;
    const avgDaysEntries = staff.filter((s) => s.avgDaysToEnrol != null);
    const avgDaysToEnrol = avgDaysEntries.length > 0
      ? avgDaysEntries.reduce((a, s) => a + s.avgDaysToEnrol, 0) / avgDaysEntries.length
      : 0;
    return { contactsTotal, advancementsTotal, conversionRate, avgDaysToEnrol };
  }, [staff]);

  if (loading && !reportData) return <Loading />;

  if (error && !reportData) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle size={48} className="text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">{error}</p>
        <button
          onClick={fetchReport}
          className="mt-4 px-4 py-2 rounded-[10px] text-sm font-semibold bg-teal-800 text-white hover:bg-teal-900 transition-colors duration-150"
        >
          <Lang w="pipelineReport.retry" />
        </button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">
          <Lang w="pipelineReport.title" />
        </h1>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <PeriodSelector
          period={periodParam}
          setPeriod={setPeriod}
          setCustomDate={setCustomDate}
          from={periodParam === "custom" ? fromParam || "" : ""}
          to={periodParam === "custom" ? toParam || "" : ""}
          t={t}
        />
        <LocationSelect
          locations={locations || []}
          value={locationParam}
          setValue={setLocation}
          t={t}
        />
      </div>

      <SummaryCards summary={summary} t={t} />

      <div className="mb-6">
        <StaffTable
          staff={staff}
          expandedStaff={expandedStaff}
          toggleExpand={toggleExpand}
          t={t}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <ContactMethodsChart staff={staff} t={t} />
        <OutcomesChart staff={staff} t={t} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <DailyTrendChart dailyTrend={dailyTrend} t={t} />
        <MonthlyHistoryChart historyData={historyData} t={t} />
      </div>
    </div>
  );
}

/**
 * PeriodSelector — Button group for Today / This Week / This Month / Custom range.
 *
 * @component
 * @param {string} period - Current period selection
 * @param {Function} setPeriod - Period setter
 * @param {Function} setCustomDate - Custom date field setter
 * @param {string} from - Custom start date
 * @param {string} to - Custom end date
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function PeriodSelector({ period, setPeriod, setCustomDate, from, to, t }) {
  const options = [
    { key: "today", labelKey: "pipelineReport.today" },
    { key: "week", labelKey: "pipelineReport.thisWeek" },
    { key: "month", labelKey: "pipelineReport.thisMonth" },
    { key: "custom", labelKey: "pipelineReport.custom" },
  ];

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <CalendarDays size={16} className="text-slate-400 flex-shrink-0" />
      {options.map((opt) => (
        <button
          key={opt.key}
          onClick={() => setPeriod(opt.key)}
          className={`
            px-3 py-1.5 rounded-[10px] text-[0.85rem] font-semibold
            transition-all duration-150 border
            ${
              period === opt.key
                ? `bg-teal-800 text-white border-teal-800 shadow-[0_2px_8px_rgba(0,0,0,0.1)]`
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-[#f8fafc]"
            }
          `}
        >
          {t(opt.labelKey)}
        </button>
      ))}
      {period === "custom" && (
        <div className="flex items-center gap-1.5 ml-1">
          <input
            type="date"
            value={from}
            onChange={(e) => setCustomDate("from", e.target.value)}
className="bg-[#f8fafc] border border-slate-200 rounded-[10px] text-slate-800 text-[0.85rem] px-2.5 py-1.5 focus:outline-none focus:border-teal-800 transition-colors duration-150"
           />
           <span className="text-slate-400 text-sm">{t("pipelineReport.to")}</span>
           <input
             type="date"
             value={to}
             onChange={(e) => setCustomDate("to", e.target.value)}
             className="bg-[#f8fafc] border border-slate-200 rounded-[10px] text-slate-800 text-[0.85rem] px-2.5 py-1.5 focus:outline-none focus:border-teal-800 transition-colors duration-150"
          />
        </div>
      )}
    </div>
  );
}

/**
 * LocationSelect — Dropdown for filtering by location.
 *
 * @component
 * @param {Array} locations - Available locations
 * @param {string} value - Current selected location ID
 * @param {Function} setValue - Location setter
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function LocationSelect({ locations, value, setValue, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = locations.find((l) => l._id === value);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-slate-200 bg-[#f8fafc] text-[0.85rem] text-slate-700 hover:bg-white hover:border-slate-300 transition-all duration-150"
      >
        <MapPin size={14} className="text-slate-400" />
        <span className="font-medium">
          {selected ? selected.name : t("pipelineReport.allLocations")}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50 overflow-hidden min-w-[180px]">
          <div
            onClick={() => {
              setValue("");
              setIsOpen(false);
            }}
            className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] ${
              !value
                ? `bg-teal-50 border-l-teal-800 font-bold text-slate-900`
                : `border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300 font-semibold text-slate-600`
            }`}
          >
            {t("pipelineReport.allLocations")}
          </div>
          {locations.map((loc) => (
            <div
              key={loc._id}
              onClick={() => {
                setValue(loc._id);
                setIsOpen(false);
              }}
              className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] ${
                value === loc._id
                  ? `bg-teal-50 border-l-teal-800 font-bold text-slate-900`
                  : `border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300 font-semibold text-slate-600`
              }`}
            >
              {loc.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * SummaryCards — Row of 4 KPI summary cards.
 *
 * @component
 * @param {Object} summary - Aggregated summary data
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function SummaryCards({ summary, t }) {
  const cards = [
    {
      labelKey: "pipelineReport.totalContacts",
      value: summary.contactsTotal || 0,
      icon: Phone,
      color: "#3b82f6",
    },
    {
      labelKey: "pipelineReport.stageAdvances",
      value: summary.advancementsTotal || 0,
      icon: TrendingUp,
      color: "#10b981",
    },
    {
      labelKey: "pipelineReport.conversionRate",
      value: `${Math.round((summary.conversionRate || 0) * 100)}%`,
      icon: Target,
      color: "#8b5cf6",
    },
    {
      labelKey: "pipelineReport.avgDaysToEnrol",
      value: summary.avgDaysToEnrol
        ? summary.avgDaysToEnrol.toFixed(1)
        : "-",
      icon: Clock,
      color: "#f59e0b",
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.labelKey}
            className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-8 h-8 rounded-[10px] flex items-center justify-center"
                style={{ backgroundColor: `${card.color}15` }}
              >
                <Icon size={16} style={{ color: card.color }} />
              </div>
              <span className="text-[0.8rem] font-medium text-slate-500">
                {t(card.labelKey)}
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{card.value}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * StaffTable — Sortable table of staff performance with expandable detail rows.
 *
 * @component
 * @param {Array} staff - Per-staff report data
 * @param {string|null} expandedStaff - ID of currently expanded staff member
 * @param {Function} toggleExpand - Toggle staff expansion
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function StaffTable({ staff, expandedStaff, toggleExpand, t }) {
  const [sortField, setSortField] = useState("contactsTotal");
  const [sortDir, setSortDir] = useState(-1);

  const sorted = useMemo(() => {
    if (!staff.length) return [];
    return [...staff].sort((a, b) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return (bVal - aVal) * sortDir;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDir;
    });
  }, [staff, sortField, sortDir]);

  function handleSort(field) {
    if (sortField === field) setSortDir((d) => d * -1);
    else {
      setSortField(field);
      setSortDir(-1);
    }
  }

  const columns = [
    { field: "staffName", labelKey: "pipelineReport.staff", numeric: false },
    { field: "contactsTotal", labelKey: "pipelineReport.contacts", numeric: true },
    { field: "stageAdvancements", labelKey: "pipelineReport.advances", numeric: true },
    { field: "leadsAdded", labelKey: "pipelineReport.leadsAdded", numeric: true },
    { field: "conversionRate", labelKey: "pipelineReport.convRate", numeric: true },
    { field: "responseRate", labelKey: "pipelineReport.responsePercent", numeric: true },
    { field: "avgDaysToEnrol", labelKey: "pipelineReport.avgDays", numeric: true },
  ];

  if (!staff.length) {
    return (
      <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 text-center">
        <UsersIcon size={32} strokeWidth={1.5} className="mx-auto mb-2 text-slate-300" />
        <p className="text-slate-400 text-sm">{t("pipelineReport.noStaffData")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#f8fafc] border-b border-slate-200">
              <th className="w-8 px-2 py-3" />
              {columns.map((col) => (
                <th
                  key={col.field}
                  onClick={() => handleSort(col.field)}
                  className={`text-[0.75rem] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors duration-150 select-none ${
                    col.numeric ? "text-right" : "text-left"
                  } ${sortField === col.field ? "text-teal-800" : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {t(col.labelKey)}
                    {sortField === col.field && (
                      <span className="text-[0.6rem]">
                        {sortDir === -1 ? "\u25BC" : "\u25B2"}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, idx) => {
              const isExpanded = expandedStaff === s.staffId;
              return (
                <React.Fragment key={s.staffId}>
                  <tr
                    onClick={() => toggleExpand(s.staffId)}
                    className={`
                      border-b border-slate-100 transition-colors duration-100 cursor-pointer
                      ${idx % 2 === 0 ? "bg-white" : "bg-[#fafbfc]"}
                      hover:bg-[#f1f5f9]
                      ${isExpanded ? "bg-teal-50" : ""}
                    `}
                  >
                    <td className="px-2 py-2.5">
                      {isExpanded ? (
                        <ChevronUp size={14} className="text-slate-400" />
                      ) : (
                        <ChevronDown size={14} className="text-slate-400" />
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="font-semibold text-slate-800 text-[0.85rem]">
                        {s.staffName}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[0.85rem] font-semibold text-slate-700">
                      {s.contactsTotal || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[0.85rem] font-semibold text-slate-700">
                      {s.stageAdvancements || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[0.85rem] font-semibold text-slate-700">
                      {s.leadsAdded || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[0.75rem] font-bold ${
                          (s.conversionRate || 0) >= 0.25
                            ? "bg-emerald-50 text-emerald-700"
                            : (s.conversionRate || 0) >= 0.1
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {Math.round((s.conversionRate || 0) * 100)}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[0.75rem] font-bold ${
                          (s.responseRate || 0) >= 0.6
                            ? "bg-emerald-50 text-emerald-700"
                            : (s.responseRate || 0) >= 0.4
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {Math.round((s.responseRate || 0) * 100)}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[0.85rem] text-slate-600">
                      {s.avgDaysToEnrol ? s.avgDaysToEnrol.toFixed(1) : "-"}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-[#fafbfc]">
                      <td colSpan={8} className="px-6 py-4">
                        <StaffExpandedDetail data={s} t={t} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * StaffExpandedDetail — Expanded detail panel for a single staff member.
 * Shows contact method breakdown, outcome breakdown, stage progression, and leads by stage.
 *
 * @component
 * @param {Object} data - Per-staff report data
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function StaffExpandedDetail({ data, t }) {
  const methodEntries = Object.entries(data.contactsByMethod || {}).filter(
    ([, v]) => v > 0
  );
  const outcomeEntries = Object.entries(data.contactsByOutcome || {}).filter(
    ([, v]) => v > 0
  );
  const progressionEntries = Object.entries(data.stageProgression || {});
  const maxMethod = Math.max(...methodEntries.map(([, v]) => v), 1);
  const maxOutcome = Math.max(...outcomeEntries.map(([, v]) => v), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Contact Methods */}
      <div>
        <h4 className="text-[0.8rem] font-bold text-slate-700 uppercase tracking-wider mb-3">
          {t("pipelineReport.contactMethods")}
        </h4>
        <div className="space-y-2">
          {methodEntries.length === 0 && (
            <p className="text-slate-400 text-sm">{t("pipelineReport.noContacts")}</p>
          )}
          {methodEntries.map(([method, count]) => {
            const Icon = METHOD_ICONS[method] || FileText;
            const color = METHOD_COLORS[method] || "#64748b";
            return (
              <div key={method} className="flex items-center gap-2">
                <span className="text-[0.8rem] text-slate-600 w-16 text-right font-medium">
                  {t(METHOD_LANG_KEYS[method] || method)}
                </span>
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${(count / maxMethod) * 100}%`,
                      backgroundColor: color,
                      minWidth: count > 0 ? "8px" : "0",
                    }}
                  />
                </div>
                <span className="text-[0.8rem] font-bold text-slate-700 w-8 text-right">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Outcomes */}
      <div>
        <h4 className="text-[0.8rem] font-bold text-slate-700 uppercase tracking-wider mb-3">
          {t("pipelineReport.outcomes")}
        </h4>
        <div className="space-y-2">
          {outcomeEntries.length === 0 && (
            <p className="text-slate-400 text-sm">{t("pipelineReport.noOutcomes")}</p>
          )}
          {outcomeEntries.map(([outcome, count]) => {
            const color = OUTCOME_COLORS[outcome] || "#64748b";
            return (
              <div key={outcome} className="flex items-center gap-2">
                <span className="text-[0.8rem] text-slate-600 w-24 text-right font-medium truncate">
                  {t(OUTCOME_LANG_KEYS[outcome] || outcome)}
                </span>
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${(count / maxOutcome) * 100}%`,
                      backgroundColor: color,
                      minWidth: count > 0 ? "8px" : "0",
                    }}
                  />
                </div>
                <span className="text-[0.8rem] font-bold text-slate-700 w-8 text-right">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Progression */}
      <div>
        <h4 className="text-[0.8rem] font-bold text-slate-700 uppercase tracking-wider mb-3">
          {t("pipelineReport.stageProgression")}
        </h4>
        <div className="space-y-1.5">
          {STAGE_ORDER.slice(0, -1).map((stage, i) => {
            const nextStage = STAGE_ORDER[i + 1];
            const key = `${stage}\u2192${nextStage}`;
            const prog = progressionEntries.find(
              ([k]) => k === key || k === `${stage}→${nextStage}`
            );
            const count = prog ? prog[1].count : 0;
            const avgDays = prog ? prog[1].avgDays : null;
            return (
              <div key={key} className="flex items-center gap-2 text-[0.8rem]">
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium"
                  style={{
                    backgroundColor: `${STAGE_COLORS[stage]}15`,
                    color: STAGE_COLORS[stage],
                  }}
                >
                  {t(STAGE_LANG_KEYS[stage])}
                </span>
                <ArrowRight size={12} className="text-slate-300" />
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium"
                  style={{
                    backgroundColor: `${STAGE_COLORS[nextStage]}15`,
                    color: STAGE_COLORS[nextStage],
                  }}
                >
                  {t(STAGE_LANG_KEYS[nextStage])}
                </span>
                <span className="ml-auto font-bold text-slate-700">{count}</span>
                {avgDays !== null && (
                  <span className="text-slate-400 text-[0.7rem]">
                    ({avgDays.toFixed(1)}d {t("pipelineReport.avgDays").toLowerCase()})
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <h4 className="text-[0.8rem] font-bold text-slate-700 uppercase tracking-wider mt-4 mb-2">
          {t("pipelineReport.leadsByStage")}
        </h4>
        <div className="flex gap-1.5 flex-wrap">
          {STAGE_ORDER.map((stage) => {
            const count = (data.leadsByStage || {})[stage] || 0;
            return (
              <span
                key={stage}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[0.75rem] font-bold"
                style={{
                  backgroundColor: `${STAGE_COLORS[stage]}15`,
                  color: STAGE_COLORS[stage],
                }}
              >
                {t(STAGE_LANG_KEYS[stage])}: {count}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * ContactMethodsChart — Bar chart showing contacts per staff member, grouped by method.
 *
 * @component
 * @param {Array} staff - Per-staff report data
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function ContactMethodsChart({ staff, t }) {
  const methodKeys = Object.keys(METHOD_LANG_KEYS);

  const chartData = useMemo(() => {
    if (!staff.length) return null;
    return {
      labels: staff.map((s) => s.staffName?.split(" ")[0] || t("pipelineReport.staff")),
      datasets: methodKeys.map((method) => ({
        label: t(METHOD_LANG_KEYS[method]),
        data: staff.map(
          (s) => (s.contactsByMethod || {})[method] || 0
        ),
        backgroundColor: `${METHOD_COLORS[method]}cc`,
        borderRadius: 4,
        borderSkipped: false,
      })),
    };
  }, [staff, t]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 2,
          useBorderRadius: true,
          padding: 12,
          font: { size: 11 },
          color: "#64748b",
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: "#64748b", font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        stacked: true,
        ticks: { color: "#94a3b8", font: { size: 11 } },
        grid: { color: "#f1f5f9" },
      },
    },
  };

  return (
    <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-4">
      <h3 className="text-[0.85rem] font-bold text-slate-700 mb-3">
        {t("pipelineReport.contactsByMethod")}
      </h3>
      <div style={{ height: 280 }}>
        {chartData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            {t("pipelineReport.noDataAvailable")}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * OutcomesChart — Stacked bar chart showing outcomes per staff member.
 *
 * @component
 * @param {Array} staff - Per-staff report data
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function OutcomesChart({ staff, t }) {
  const outcomeKeys = Object.keys(OUTCOME_LANG_KEYS);

  const chartData = useMemo(() => {
    if (!staff.length) return null;
    return {
      labels: staff.map((s) => s.staffName?.split(" ")[0] || t("pipelineReport.staff")),
      datasets: outcomeKeys.map((outcome) => ({
        label: t(OUTCOME_LANG_KEYS[outcome]),
        data: staff.map(
          (s) => (s.contactsByOutcome || {})[outcome] || 0
        ),
        backgroundColor: `${OUTCOME_COLORS[outcome]}cc`,
        borderRadius: 4,
        borderSkipped: false,
      })),
    };
  }, [staff, t]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 2,
          useBorderRadius: true,
          padding: 10,
          font: { size: 10 },
          color: "#64748b",
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: "#64748b", font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        stacked: true,
        ticks: { color: "#94a3b8", font: { size: 11 } },
        grid: { color: "#f1f5f9" },
      },
    },
  };

  return (
    <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-4">
      <h3 className="text-[0.85rem] font-bold text-slate-700 mb-3">
        {t("pipelineReport.outcomesBreakdown")}
      </h3>
      <div style={{ height: 280 }}>
        {chartData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            {t("pipelineReport.noDataAvailable")}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * DailyTrendChart — Line chart showing daily contacts and advancements.
 *
 * @component
 * @param {Array} dailyTrend - Array of { date, contacts, advancements } objects
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function DailyTrendChart({ dailyTrend, t }) {
  const chartData = useMemo(() => {
    if (!dailyTrend || !dailyTrend.length) return null;
    return {
      labels: dailyTrend.map((d) => {
        const dt = new Date(d.date);
        return `${dt.getDate()}/${dt.getMonth() + 1}`;
      }),
      datasets: [
        {
          label: t("pipelineReport.contacts"),
          data: dailyTrend.map((d) => d.contacts || 0),
          borderColor: "#3b82f6",
          backgroundColor: "#3b82f620",
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          pointBackgroundColor: "#3b82f6",
        },
        {
          label: t("pipelineReport.advances"),
          data: dailyTrend.map((d) => d.advancements || 0),
          borderColor: "#10b981",
          backgroundColor: "#10b98120",
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          pointBackgroundColor: "#10b981",
        },
      ],
    };
  }, [dailyTrend, t]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 2,
          useBorderRadius: true,
          padding: 16,
          font: { size: 11 },
          color: "#64748b",
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { color: "#64748b", font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        ticks: { color: "#94a3b8", font: { size: 11 } },
        grid: { color: "#f1f5f9" },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-4">
      <h3 className="text-[0.85rem] font-bold text-slate-700 mb-3">
        {t("pipelineReport.dailyTrend")}
      </h3>
      <div style={{ height: 280 }}>
        {chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            {t("pipelineReport.noDailyData")}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * MonthlyHistoryChart — Bar chart comparing monthly totals over the last 6 months.
 *
 * @component
 * @param {Array} historyData - Monthly aggregated snapshots
 * @param {Function} t - Translation function
 * @author Forge, opencode + glm-5
 * @date 2026-04-16
 * @version 1.1.0
 */
function MonthlyHistoryChart({ historyData, t }) {
  const chartData = useMemo(() => {
    if (!historyData || !historyData.length) return null;
    return {
      labels: historyData.map((h) => {
        const [y, m] = h.month.split("-");
        const dt = new Date(parseInt(y), parseInt(m) - 1);
        return dt.toLocaleDateString("en", { month: "short", year: "2-digit" });
      }),
      datasets: [
        {
          label: t("pipelineReport.contacts"),
          data: historyData.map((h) => h.contactsTotal || 0),
          backgroundColor: "#3b82f6cc",
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: t("pipelineReport.advances"),
          data: historyData.map((h) => h.stageAdvancements || 0),
          backgroundColor: "#10b981cc",
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: t("pipelineReport.leadsAdded"),
          data: historyData.map((h) => h.leadsAdded || 0),
          backgroundColor: "#f59e0bcc",
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [historyData, t]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 2,
          useBorderRadius: true,
          padding: 16,
          font: { size: 11 },
          color: "#64748b",
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { color: "#64748b", font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        ticks: { color: "#94a3b8", font: { size: 11 } },
        grid: { color: "#f1f5f9" },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-4">
      <h3 className="text-[0.85rem] font-bold text-slate-700 mb-3">
        {t("pipelineReport.monthlyHistory")}
      </h3>
      <div style={{ height: 280 }}>
        {chartData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            {t("pipelineReport.noHistoryData")}
          </div>
        )}
      </div>
    </div>
  );
}